import React, {
    ReactElement, useState,
} from "react";
import { Form } from "react-bootstrap";
import CodeBlock from "../../../../../components/code-block/CodeBlock";
import useArticleContext from "../../../../../contexts/HelpContext";
import { SettingsProps } from "../../base";
import {
    GetHubSpokeStatusDocument,
    useDisableHubSpokeMutation,
    useEnableHubSpokeMutation, useGetHubMetadataLazyQuery, useGetHubSpokeStatusQuery, useGetHubSpokeTokenLazyQuery,
} from "../../../../../__generated__/api";
import { LoadingIcon } from "../../../../../components/loading/Loading";
import Confirm from "../../../../../components/confirm/Confirm";

export function HubAndSpoke({
    shouldShow,
    onHide,
}: {
    shouldShow: boolean,
    onHide: () => void,
}): ReactElement {
    const [remoteToken, setRemoteToken] = useState("");
    const [getTokenData, { data: tokenData, loading: isTokenDataLoading }] = useGetHubMetadataLazyQuery();
    const [enableHubAndSpoke] = useEnableHubSpokeMutation();

    const loadToken = () => {
        (async () => {
            await getTokenData({
                variables: {
                    token: remoteToken.trim(),
                },
            });
        })();
    };

    const confirmToken = () => {
        (async () => {
            await enableHubAndSpoke({
                refetchQueries: [
                    { query: GetHubSpokeStatusDocument },
                ],
                variables: {
                    settings: {
                        destinationToken: remoteToken.trim(),
                    },
                },
            });
            onHide();
        })();
    };

    return (
        <div className="flex-grow-1">
            <Confirm
                shouldShow={shouldShow}
                onConfirm={tokenData ? confirmToken : loadToken}
                confirmText={tokenData ? "Confirm" : "Next"}
                onHide={onHide}
            >
                <div>
                    {tokenData ? (
                        <div>
                            <p>Do you want to forward all messages from this workspace to the following Foqal Workspace</p>
                            <div className="alert alert-danger p-2">
                                {tokenData.getHubData?.name}
                            </div>
                        </div>
                    ) : (
                        <div>
                            {isTokenDataLoading ? <LoadingIcon /> : (
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">Hub Token</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        value={remoteToken}
                                        onChange={event => setRemoteToken(event.target.value)}
                                    />
                                    <Form.Text>
                                        Please enter the Hub Token from the other workspace.
                                    </Form.Text>
                                </Form.Group>
                            )}
                        </div>
                    )}

                </div>
            </Confirm>
        </div>
    );
}
export default function Forwarding(): ReactElement {
    useArticleContext("urn:fq:article:3c07322acde14bea8b0a5e480075acb0");

    const [shouldStartForwarding, setShouldStartForwarding] = useState(false);
    const [showDisable, setShowDisable] = useState(false);

    const { data } = useGetHubSpokeStatusQuery();

    const [getToken, { data: tokenData, loading: isTokenLoading }] = useGetHubSpokeTokenLazyQuery();
    const [disableHubSpoke] = useDisableHubSpokeMutation();

    const onDisableHubSpoke = async () => {
        await disableHubSpoke({
            refetchQueries: [
                { query: GetHubSpokeStatusDocument },
            ],
            variables: {
                settings: {},
            },
        });
        setShowDisable(false);
    };

    return (
        <div className="flex-grow-1">
            <HubAndSpoke shouldShow={shouldStartForwarding} onHide={() => setShouldStartForwarding(false)} />

            <Confirm
                shouldShow={showDisable}
                onConfirm={() => onDisableHubSpoke()}
                confirmText="Confirm Disable"
                onHide={() => setShowDisable(false)}
                confirmVariant="danger"
            >
                <div>
                    Do you want to disable Hub and Spoke and stop forwarding messages to
                    {" "}
                    {data?.getHubSpokeStatus?.forwardingTo?.name}
                </div>
            </Confirm>
            <div className="border p-3 rounded bg-white mb-4">
                <h5 className="mb-3 text-light">
                    Hub and Spoke
                </h5>
                <p className="text-light">
                    Hub and spoke allows you to forward messages between multiple Slack workspaces, channels, or sources.
                    If you have multiple Slack workspace and want them to all centralize in one Foqal Agent workspace, use
                    Hub and Spoke to help. Meaning, your customer can send a message in one workspaces, and your
                    agents can respond in another. To get started, select whether this Foqal Agent Workspace is the Hub
                    or the Spoke.
                </p>
                {data?.getHubSpokeStatus?.forwardingTo ? (
                    <div>
                        <div className="alert alert-danger p-2 d-flex justify-content-between align-items-center">
                            <div>
                                Hub and Spoke is enabled in this workspace. All messages are being forwarded to
                                {" "}
                                {data?.getHubSpokeStatus?.forwardingTo.name}
                            </div>
                            <button className="btn btn-danger" type="button" onClick={() => setShowDisable(true)}>
                                Disable
                            </button>
                        </div>
                    </div>
                ) : (

                    <>
                        <div className="w-100 d-flex column-gap-3 row-gap-3">
                            <div className="col border rounded p-3 text-center">
                                <h5>Hub</h5>
                                <p>
                                    The Hub is where the Agents work from. This is the parent
                                    workspace that will receive messages from the spokes.
                                </p>
                                {tokenData?.hubSpokeToken?.token ? (
                                    <CodeBlock shouldWrap code={tokenData?.hubSpokeToken?.token} className="my-2" />
                                ) : (
                                    <div>
                                        {isTokenLoading ? (
                                            <LoadingIcon />
                                        ) : (
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={() => getToken()}
                                            >
                                                Generate Hub Token
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="col border rounded p-3 text-center">
                                <h5>Spoke</h5>
                                <p>
                                    This is the child workspace that will forward all conversations
                                    into the Hub. This is generally where your customers talk to you.
                                </p>
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={() => setShouldStartForwarding(true)}
                                >
                                    Forward to Hub
                                </button>
                            </div>
                        </div>

                        {data?.getHubSpokeStatus?.forwardedFrom.isNotEmpty && (
                            <div className="mt-4">
                                <h5>Currently forwarded from:</h5>
                                <ul className="list-group">
                                    {data?.getHubSpokeStatus?.forwardedFrom.map(item => (
                                        <li key={item.id} className="list-group-item">
                                            {item.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                    </>
                )}

            </div>

        </div>
    );
}
